import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Mui Libraries
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import CardActionArea from '@mui/material/CardActionArea';

// Slice
import { fetchPlatform, fetchCategory } from './integrationsSlice';

// Components
import PlatformDrawer from '../../General/PlatformDrawer';

const NonintegratedPage = () => {

    const dispatch = useDispatch();
    const integrations = useSelector(state => state.integrations);

    const [currentCategory, setCurrentCategory] = useState('all');
    const [platformName, setPlatformName] = useState('all');
    const [stepNumber, setStepNumber] = useState('all');
    const [orderDrawerVisible, setOrderDrawerVisible] = useState(false);

    const platformCallback = (item) => {
        console.log(item)
        if (item === 'Shopify APP') {
            item = 'SHOPIFY'

            setPlatformName(item)
            setOrderDrawerVisible(true)
        } else if (item === 'Brightpearl' || item === 'WooCommerce' || item === 'Bigcommerce' || item === 'Api' || item === 'Skulabs' || item === 'ShipStation' || item === 'Etsy' || item === 'Zonos' || item === 'Fishbowl') {
            setPlatformName(item)
            setOrderDrawerVisible(true)
        }
    }

    const orderOnClose = (values) => {
        setOrderDrawerVisible(false);
    };

    useEffect(() => {
        const params = new URLSearchParams(window.location.search) // id=123
        let platform = params.get('platform')
        if (platform === 'Shopify APP') {
            platform = 'SHOPIFY'
        }
        let step = params.get('step')
        setPlatformName(platform)
        setStepNumber(step)
        if (platform) {
            setOrderDrawerVisible(true)
        }
    }, [])

    useEffect(() => {
        dispatch(fetchCategory());
        dispatch(fetchPlatform());
    }, [dispatch]);

    const PlatformComponent = ({ platform = [], currentCategory, searchField }) => {
        return (
            <>
                {
                    platform && platform.map((item) => {
                        if (item.name === 'Shopify APP' || item.name === 'Brightpearl' || item.name === 'WooCommerce' || item.name === 'Bigcommerce' || item.name === 'Api' || item.name === 'Skulabs' || item.name === 'ShipStation'  || item.name === 'Etsy' || item.name === 'Zonos' || item.name === 'Fishbowl') {
                            const platformDisplayName = item.name === 'Shopify APP' ? 'Shopify' : item.name === 'Api' ? 'Marketplace API' : item.name;
                            
                            return (
                                <Grid
                                    key={item.name}
                                    // --- UPGRADED RESPONSIVE BREAKPOINT GRID GRID MATRIX ---
                                    item 
                                    xs={12}    // 1 Item per row on small phones (prevents text clipping/squeezing)
                                    sm={6}     // 2 Items per row on larger mobile devices/tablets
                                    md={4}     // 3 Items per row on standard laptop screens
                                    lg={3}     // 4 Items per row on desktop workstations
                                    xl={2.4}   // 5 Items per row on ultrawide enterprise screens
                                >
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            border: '1px solid #E2E8F0',
                                            borderRadius: '16px',
                                            overflow: 'hidden',
                                            backgroundColor: '#FFFFFF',
                                            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                                            '&:hover': {
                                                borderColor: '#1976d2',
                                                boxShadow: '0 12px 20px -8px rgba(25, 118, 210, 0.12)',
                                                transform: 'translateY(-4px)'
                                            }
                                        }}
                                    >
                                        <CardActionArea 
                                            onClick={() => platformCallback(item.name)}
                                            sx={{ 
                                                p: { xs: 2, sm: 3 }, // Compact padding scales beautifully onto mobile viewports
                                                display: 'flex', 
                                                flexDirection: 'column', 
                                                alignItems: 'center', 
                                                gap: { xs: 1.5, sm: 2 } 
                                            }}
                                        >
                                            {/* Responsive bounding block preventing image logo stretching/pixel overflows */}
                                            <Box
                                                className="nonintegration--images"
                                                sx={{
                                                    width: '100%',
                                                    maxHeight: { xs: 55, sm: 70 }, // Shinks the asset height safely on phone displays
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    aspectRatio: '16/9', // Guarantees structural card layout alignment
                                                    '& img': {
                                                        maxWidth: '100%',
                                                        maxHeight: '100%',
                                                        objectFit: 'contain',
                                                        transition: 'transform 0.2s ease-in-out'
                                                    }
                                                }}
                                            >
                                                <img
                                                    className="integration--logo"
                                                    src={item.image}
                                                    data-src={item.image}
                                                    alt={item.name}
                                                    title={platformDisplayName}
                                                />
                                            </Box>
                                            
                                            {/* Micro-responsive Typography platform title wrapper text */}
                                            <Typography
                                                variant="subtitle2"
                                                title={platformDisplayName}
                                                sx={{
                                                    fontWeight: 700,
                                                    color: '#1E293B',
                                                    textAlign: 'center',
                                                    width: '100%',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    fontSize: { xs: '12.5px', sm: '13.5px' } // Adjusts typographic engine weight based on context
                                                }}
                                            >
                                                {platformDisplayName}
                                            </Typography>
                                        </CardActionArea>
                                    </Paper>
                                </Grid>
                            )
                        }
                        return null;
                    })
                }
            </>
        )
    }

    return (
        <Box sx={{ width: '100%', p: { xs: 0.5, sm: 1 } }}>
            {integrations && integrations.platform.loading === true ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                    <CircularProgress size={32} sx={{ color: '#1976d2' }} />
                </Box>
            ) : (
                <Grid container spacing={{ xs: 2, sm: 3 }}> {/* Tighter gaps on standard phones prevent grid breakage */}
                    <PlatformComponent
                        platform={integrations && integrations.platform.data}
                        currentCategory={currentCategory}
                    />
                </Grid>
            )}

            {orderDrawerVisible && (
                <PlatformDrawer
                    visible={orderDrawerVisible}
                    onClose={orderOnClose}
                    platform={platformName}
                    stepNumber={stepNumber}
                />
            )}
        </Box>
    );
};

export default NonintegratedPage;